/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { DebounceTime } from '../../../Constants/App';
import { Icons } from '../../../Constants/Icons';
import theme from '../../../Themes/theme';
import { SearchFieldContainer, SearchInput } from '../../Layout/styles';

interface IGlobalSearchBarProps {
  /** Debounced value owned by the parent. Used to reset the input when the parent clears the search. */
  value: string;
  /** Fires after the user stops typing for DebounceTime ms. */
  onSearch: (searchText: string) => void;
  placeholder?: string;
}

/**
 * Free-text search field rendered above a data grid. Mirrors the Job Management
 * Dashboard search bar (All Jobs screen) so every list screen searches the same way:
 * the parent owns the debounced value and refetches server side.
 */
const GlobalSearchBar = ({ value, onSearch, placeholder }: IGlobalSearchBarProps): JSX.Element => {
  const { t } = useTranslation(['oneTimeJob', 'altTexts']);
  const [inputValue, setInputValue] = useState(value);

  // Keep the input in sync when the parent resets the search (e.g. on tab switch).
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const debouncedSearch = useMemo(() => debounce((text: string) => onSearch(text), DebounceTime), [onSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = (text: string): void => {
    setInputValue(text);
    debouncedSearch(text);
  };

  return (
    <SearchFieldContainer
      sx={{ marginBottom: '1rem', border: inputValue ? `0.0625rem solid ${theme.palette.primary.dark}` : '' }}>
      <SearchInput
        placeholder={placeholder ?? t('oneTimeJob:search')}
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
      />
      <img src={Icons.SearchIcon} alt={t('altTexts:search')} />
    </SearchFieldContainer>
  );
};

export default GlobalSearchBar;
